import React, { useState } from 'react';

export default function CashBridgeWaterfall({ cashBridge, year, availableYears, onYearChange }) {
    const [hoveredBar, setHoveredBar] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    if (!cashBridge) {
        return (
            <div style={{
                padding: '32px',
                background: 'var(--surface-info)',
                borderRadius: '16px',
                border: '2px dashed var(--surface-info-border)',
                textAlign: 'center',
                color: 'var(--surface-info-text)'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Data Available</div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Cash flow movement data not available for this period.</div>
            </div>
        );
    }

    const { steps, reconciliation } = cashBridge;

    // Check if we have any data
    const hasData = steps && steps.some(s => s.value !== null && s.value !== undefined);
    if (!hasData) {
        return (
            <div style={{
                padding: '32px',
                background: 'var(--surface-warning)',
                borderRadius: '16px',
                border: '2px dashed var(--surface-warning-border)',
                textAlign: 'center',
                color: 'var(--surface-warning-text)'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Insufficient Data</div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Cannot generate cash flow movement for {year}.</div>
            </div>
        );
    }

    // Calculate cumulative values for waterfall positioning
    const calculateWaterfallData = () => {
        let runningTotal = 0;
        const waterfallData = [];

        steps.forEach((step) => {
            // For total bars (Beginning/Ending Cash), always render even if null
            if (step.type === 'total') {
                const cashValue = step.value ?? 0;
                const isMissing = step.value === null || step.value === undefined;

                waterfallData.push({
                    ...step,
                    start: 0,
                    end: cashValue,
                    height: Math.abs(cashValue),
                    isVisible: true,
                    isTotal: true,
                    isMissing: isMissing
                });

                if (step.key === 'beginCash') {
                    runningTotal = cashValue;
                }
                return;
            }

            // For delta bars, handle missing data
            if (step.value === null || step.value === undefined) {
                waterfallData.push({
                    ...step,
                    start: runningTotal,
                    end: runningTotal,
                    height: 0,
                    isVisible: true,
                    isMissing: true
                });
                return;
            }

            // Delta bars float from running total
            const start = runningTotal;
            const end = runningTotal + step.value;
            waterfallData.push({
                ...step,
                start: Math.min(start, end),
                end: Math.max(start, end),
                height: Math.abs(step.value),
                isPositive: step.value >= 0,
                isVisible: true,
                isTotal: false,
                isMissing: false
            });
            runningTotal = end;
        });

        return waterfallData;
    };

    const waterfallData = calculateWaterfallData();

    // Find min and max for scaling
    const allValues = waterfallData.flatMap(d => [d.start, d.end]).filter(v => v !== null && v !== undefined);
    const minValue = Math.min(0, ...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;
    const padding = range * 0.1;

    const formatValue = (value) => {
        if (value === null || value === undefined) return 'N/A';
        const absValue = Math.abs(value);
        return `₹${absValue.toFixed(2)} Cr`;
    };

    const formatValueWithSign = (value) => {
        if (value === null || value === undefined) return 'N/A';
        const sign = value >= 0 ? '+' : '';
        return `${sign}₹${value.toFixed(2)} Cr`;
    };

    // Chart dimensions
    const chartHeight = 300;
    const chartWidth = 800;
    const barWidth = 80;
    const gap = 40;

    // Calculate bar positions
    const totalWidth = waterfallData.length * (barWidth + gap);
    const xOffset = (chartWidth - totalWidth) / 2;

    const getBarColor = (step, isPositive, isHovered = false) => {
        if (step.type === 'total') {
            return isHovered ? '#3b82f6' : '#60a5fa';
        }
        if (isPositive) {
            return isHovered ? '#059669' : '#10b981';
        }
        return isHovered ? '#dc2626' : '#ef4444';
    };

    const getBarGradient = (step, isPositive, index) => {
        if (step.type === 'total') {
            return `url(#gradient-total-${index})`;
        }
        return isPositive ? `url(#gradient-positive-${index})` : `url(#gradient-negative-${index})`;
    };

    const getIconForStep = (key) => {
        const icons = {
            'beginCash': '💰',
            'ocf': '🔄',
            'ar': '📋',
            'inv': '📦',
            'ap': '💳',
            'capex': '🏭',
            'div': '💸',
            'other': '📊',
            'endCash': '🎯'
        };
        return icons[key] || '📌';
    };

    // Calculate reconciliation
    const showReconciliation = reconciliation && reconciliation.reportedEndCash !== null && reconciliation.computedEndCash !== null;
    const reconciliationDiff = showReconciliation ? reconciliation.diff : 0;
    const reconciliationPct = showReconciliation && reconciliation.reportedEndCash !== 0
        ? (reconciliationDiff / reconciliation.reportedEndCash) * 100 : 0;
    const isReconciled = showReconciliation && (Math.abs(reconciliationPct) < 2 || Math.abs(reconciliationDiff) < 10);

    return (
        <div style={{ marginBottom: '32px' }}>
            {/* Enhanced Header */}
            <div style={{
                background: 'var(--bg-3)',
                padding: '20px 24px',
                borderRadius: '16px 16px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '32px' }}>💰</div>
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: 'var(--text-1)',
                            margin: 0,
                            marginBottom: '4px'
                        }}>
                            Cash Flow Movement
                        </h2>
                        <div style={{
                            fontSize: '14px',
                            color: 'var(--text-2)',
                            fontWeight: '500'
                        }}>
                            Waterfall Analysis • FY {year}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {availableYears && availableYears.length > 1 && (
                        <select
                            value={year}
                            onChange={(e) => onYearChange && onYearChange(e.target.value)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: '2px solid var(--border)',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                backgroundColor: 'var(--card-bg)',
                                color: 'var(--text-1)',
                                outline: 'none'
                            }}
                        >
                            {availableYears.map(y => (
                                <option key={y} value={y}>FY {y}</option>
                            ))}
                        </select>
                    )}
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            backgroundColor: showDetails ? '#60a5fa' : '#475569',
                            color: '#ffffff'
                        }}
                    >
                        {showDetails ? '📊 Hide Details' : '📈 Show Details'}
                    </button>
                </div>
            </div>

            {/* Chart */}
            <div style={{
                backgroundColor: '#ffffff',
                padding: '32px 24px',
                borderRadius: '0 0 16px 16px',
                border: '2px solid #e5e7eb',
                borderTop: 'none',
                marginBottom: '16px',
                overflowX: 'auto',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
                <svg
                    width="100%"
                    height={chartHeight + 120}
                    viewBox={`0 0 ${chartWidth} ${chartHeight + 120}`}
                    style={{ minWidth: '700px' }}
                >
                    <defs>
                        {waterfallData.map((item, index) => {
                            if (item.type === 'total') {
                                return (
                                    <linearGradient key={`gradient-total-${index}`} id={`gradient-total-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                                    </linearGradient>
                                );
                            } else if (item.isPositive) {
                                return (
                                    <linearGradient key={`gradient-positive-${index}`} id={`gradient-positive-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: '#34d399', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
                                    </linearGradient>
                                );
                            } else {
                                return (
                                    <linearGradient key={`gradient-negative-${index}`} id={`gradient-negative-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: '#f87171', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
                                    </linearGradient>
                                );
                            }
                        })}
                        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                            <feOffset dx="0" dy="2" result="offsetblur" />
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.3" />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                        const value = minValue - padding + ratio * (range + 2 * padding);
                        const y = chartHeight - (ratio * chartHeight);
                        const isZeroLine = Math.abs(value) < (range * 0.02);
                        return (
                            <g key={i}>
                                <line
                                    x1="50"
                                    y1={y}
                                    x2={chartWidth - 20}
                                    y2={y}
                                    stroke={isZeroLine ? '#94a3b8' : '#e5e7eb'}
                                    strokeWidth={isZeroLine ? '2' : '1'}
                                    strokeDasharray={isZeroLine ? '5,5' : '3,3'}
                                    opacity={isZeroLine ? '0.8' : '0.6'}
                                />
                                <text
                                    x="45"
                                    y={y}
                                    textAnchor="end"
                                    alignmentBaseline="middle"
                                    fontSize="12"
                                    fontWeight={isZeroLine ? '600' : '400'}
                                    fill={isZeroLine ? '#475569' : '#94a3b8'}
                                >
                                    {formatValue(value)}
                                </text>
                            </g>
                        );
                    })}

                    {waterfallData.map((item, index) => {
                        if (!item.isVisible) return null;

                        const x = xOffset + index * (barWidth + gap);
                        const isHovered = hoveredBar === index;

                        const scaleY = (val) => {
                            const normalized = (val - minValue + padding) / (range + 2 * padding);
                            return chartHeight - (normalized * chartHeight);
                        };

                        const yStart = scaleY(item.start);
                        const yEnd = scaleY(item.end);
                        const barHeight = Math.abs(yEnd - yStart);
                        const barY = Math.min(yStart, yEnd);

                        return (
                            <g
                                key={item.key}
                                onMouseEnter={() => setHoveredBar(index)}
                                onMouseLeave={() => setHoveredBar(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {!item.isMissing && (
                                    <rect
                                        x={x + 50}
                                        y={barY}
                                        width={barWidth}
                                        height={barHeight}
                                        fill="black"
                                        opacity="0.1"
                                        filter="url(#shadow)"
                                    />
                                )}

                                <rect
                                    x={x + 50}
                                    y={barY}
                                    width={barWidth}
                                    height={Math.max(barHeight, 2)}
                                    fill={item.isMissing ? '#d1d5db' : getBarGradient(item, item.isPositive, index)}
                                    stroke={isHovered ? '#1f2937' : getBarColor(item, item.isPositive, false)}
                                    strokeWidth={isHovered ? '3' : '2'}
                                    rx="4"
                                    ry="4"
                                    opacity={item.isMissing ? '0.5' : '0.95'}
                                >
                                    {item.tooltip && (<title>{item.tooltip}</title>)}
                                </rect>

                                {item.type === 'delta' && index < waterfallData.length - 1 && !item.isMissing && (
                                    <line
                                        x1={x + 50 + barWidth}
                                        y1={scaleY(item.end)}
                                        x2={x + 50 + barWidth + gap}
                                        y2={scaleY(item.end)}
                                        stroke="#64748b"
                                        strokeWidth="2"
                                        strokeDasharray="6,4"
                                        opacity="0.4"
                                    />
                                )}

                                <text
                                    x={x + 50 + barWidth / 2}
                                    y={barY - 28}
                                    textAnchor="middle"
                                    fontSize="20"
                                >
                                    {getIconForStep(item.key)}
                                </text>

                                {!item.isMissing && (
                                    <>
                                        <rect
                                            x={x + 50 + barWidth / 2 - 45}
                                            y={barY - 20}
                                            width="90"
                                            height="20"
                                            fill="white"
                                            rx="10"
                                            opacity={isHovered ? '0.95' : '0.85'}
                                            stroke={getBarColor(item, item.isPositive, isHovered)}
                                            strokeWidth="1.5"
                                        />
                                        <text
                                            x={x + 50 + barWidth / 2}
                                            y={barY - 7}
                                            textAnchor="middle"
                                            fontSize={isHovered ? '13' : '12'}
                                            fontWeight="700"
                                            fill={getBarColor(item, item.isPositive, true)}
                                        >
                                            {item.isTotal ? formatValue(item.value) : formatValueWithSign(item.value)}
                                        </text>
                                    </>
                                )}

                                {item.isMissing && (
                                    <text
                                        x={x + 50 + barWidth / 2}
                                        y={barY - 8}
                                        textAnchor="middle"
                                        fontSize="14"
                                        fontWeight="700"
                                        fill="#9ca3af"
                                    >
                                        N/A
                                    </text>
                                )}

                                <text
                                    x={x + 50 + barWidth / 2}
                                    y={chartHeight + 25}
                                    textAnchor="middle"
                                    fontSize={isHovered ? '12' : '11'}
                                    fontWeight="600"
                                    fill={isHovered ? '#1f2937' : '#475569'}
                                >
                                    {item.label.split(' ').slice(0, 2).join(' ')}
                                </text>
                                <text
                                    x={x + 50 + barWidth / 2}
                                    y={chartHeight + 40}
                                    textAnchor="middle"
                                    fontSize={isHovered ? '12' : '11'}
                                    fontWeight="600"
                                    fill={isHovered ? '#1f2937' : '#475569'}
                                >
                                    {item.label.split(' ').slice(2).join(' ')}
                                </text>

                                {isHovered && item.tooltip && (
                                    <g>
                                        <rect
                                            x={x + 50 + barWidth / 2 - 100}
                                            y={chartHeight + 55}
                                            width="200"
                                            height="50"
                                            fill="#1f2937"
                                            rx="8"
                                            opacity="0.95"
                                        />
                                        <text
                                            x={x + 50 + barWidth / 2}
                                            y={chartHeight + 75}
                                            textAnchor="middle"
                                            fontSize="11"
                                            fill="#ffffff"
                                            fontWeight="500"
                                        >
                                            {item.tooltip.substring(0, 40)}
                                        </text>
                                        <text
                                            x={x + 50 + barWidth / 2}
                                            y={chartHeight + 90}
                                            textAnchor="middle"
                                            fontSize="11"
                                            fill="#ffffff"
                                            fontWeight="500"
                                        >
                                            {item.tooltip.substring(40, 80)}
                                        </text>
                                    </g>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            {showReconciliation && (
                <div style={{
                    padding: '20px 24px',
                    background: isReconciled
                        ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                        : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: `2px solid ${isReconciled ? '#059669' : '#f59e0b'}`,
                    borderRadius: '12px',
                    fontSize: '14px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    marginTop: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '24px' }}>{isReconciled ? '✅' : '⚠️'}</div>
                        <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '16px' }}>
                            Cash Reconciliation {isReconciled ? 'Verified' : 'Warning'}
                        </div>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: '12px 24px',
                        color: '#374151',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        padding: '16px',
                        borderRadius: '8px'
                    }}>
                        <span style={{ fontWeight: '600' }}>Computed Ending Cash:</span>
                        <span style={{ fontWeight: '700', color: '#1f2937' }}>{formatValue(reconciliation.computedEndCash)}</span>

                        <span style={{ fontWeight: '600' }}>Reported Ending Cash:</span>
                        <span style={{ fontWeight: '700', color: '#1f2937' }}>{formatValue(reconciliation.reportedEndCash)}</span>

                        <span style={{ fontWeight: '600' }}>Difference:</span>
                        <span style={{ fontWeight: '700', color: isReconciled ? '#059669' : '#d97706', fontSize: '15px' }}>
                            {formatValueWithSign(reconciliation.diff)} ({reconciliationPct.toFixed(2)}%)
                        </span>
                    </div>
                    {!isReconciled && (
                        <div style={{
                            marginTop: '12px',
                            padding: '12px 16px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '8px',
                            borderLeft: '4px solid #f59e0b',
                            color: '#92400e',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '18px' }}>ℹ️</span>
                            <span>Bridge does not fully reconcile. This may be due to classification differences or missing line items.</span>
                        </div>
                    )}
                </div>
            )}

            {showDetails && (
                <div style={{
                    marginTop: '20px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px solid #cbd5e1',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span style={{ fontSize: '20px' }}>📋</span>
                        Detailed Breakdown
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '12px'
                    }}>
                        {steps && steps.filter(s => !s.isMissing && s.value !== null).map((step, idx) => (
                            <div key={idx} style={{
                                padding: '16px',
                                backgroundColor: '#ffffff',
                                borderRadius: '10px',
                                border: '2px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '24px' }}>{getIconForStep(step.key)}</span>
                                    <span style={{ fontWeight: '600', color: '#475569', fontSize: '14px' }}>
                                        {step.label}
                                    </span>
                                </div>
                                <span style={{
                                    fontWeight: '700',
                                    fontSize: '15px',
                                    color: step.type === 'total' ? '#3b82f6' : (step.isPositive ? '#059669' : '#dc2626')
                                }}>
                                    {step.type === 'total' ? formatValue(step.value) : formatValueWithSign(step.value)}
                                </span>
                            </div>
                        ))}
                    </div>
                    {steps && steps.some(s => s.tooltip) && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#dbeafe',
                            borderRadius: '8px',
                            borderLeft: '4px solid #3b82f6',
                            fontSize: '13px',
                            color: '#1e40af'
                        }}>
                            <strong>💡 Tip:</strong> Hover over bars in the chart above to see detailed tooltips and explanations.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
